<script setup lang="ts">
/**
 * Top-level presentational editor for the RAW premade/custom highlights redesign — the Figma
 * "List builder card" (node 1791:99905): one bordered, rounded container with two stacked
 * sections. Premade (top): title + description + primary "Add premade" button + reorderable
 * read-only rows. Custom (bottom): title + description + outline "Add custom" button +
 * reorderable rows with inline create/edit.
 *
 * DUMB component (props down / events up, no store access). NOT the final wiring to RAW — it
 * stays the playground target until `RawHighlights.vue` becomes the thin store adapter.
 *
 * ## Where the concerns live
 * - Data operations live in the headless, kind-scoped composables `usePremadeList` and
 *   `useCustomList`. Neither knows about UI state.
 * - The sortable rows (drag projection + reorder mapping) live in `PremadeCustomEditorList`,
 *   used once per slice.
 * - UI gating lives HERE: `readonly` (hide controls) and the edit lock. The lock is a single
 *   piece of coordination state — `editing` = which custom row is open (an id), a new-item
 *   sentinel, or `null`. `isLocked = editing !== null`. It flows DOWN as `lockedId`; a row
 *   never computes policy itself. While locked, the whole Custom section forbids other
 *   actions (reorder / add / edit another) per the acceptance criteria.
 * - The draft text + trim/whitespace validation live in `HighlightItemEditor` (used for both
 *   create and edit); it only ever emits an already-valid name.
 * - The add-premade modal owns its own local selection and commits on Add.
 */
import { computed, ref } from "vue";
import type { HighlightItem } from "@/features/experience-shared/highlights-v2/types/highlight-item";
import {
  usePremadeList,
  type PremadeOption,
} from "@/features/experience-shared/highlights-v2/composables/usePremadeList";
import { useCustomList } from "@/features/experience-shared/highlights-v2/composables/useCustomList";
import HighlightItemEditor from "@/features/experience-shared/highlights-v2/components/HighlightItemEditor.vue";
import PremadeCustomEditorList from "@/features/experience-shared/highlights-v2/components/PremadeCustomEditorList.vue";
import AddPremadeModal from "@/features/experience-shared/highlights-v2/components/AddPremadeModal.vue";
import NovaButton from "@/ui-kit/NovaButton/NovaButton.vue";
import NovaIcon from "@/ui-kit/NovaIcon/NovaIcon.vue";
import type { ExperiencePremadeItem } from "@/composables/useExperienceMasterDataApi";
import type { HierarchicalGroup } from "@/types/generated/ExperienceMasterDataApi";

export interface Props {
  /** All premade masterdata options — grouped in the modal by their hierarchical_group_code. */
  masterdataOptions?: ExperiencePremadeItem[];
  /** Group metadata (code -> { name, archived }) used for the modal's accordion titles + sort. */
  hierarchicalGroups?: Map<string, HierarchicalGroup>;
  readonly?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  masterdataOptions: () => [],
  hierarchicalGroups: () => new Map(),
  readonly: false,
});

// The list is a v-model — the composables read and write this ref directly, and Vue emits
// `update:items` upstream on each change.
const items = defineModel<HighlightItem[]>("items", { required: true });

const { premadeItems, addedPremadeCodes, syncPremade, removePremade, reorderPremade } = usePremadeList(items);
const { customItems, addCustom, renameCustom, removeCustom, reorderCustom } = useCustomList(items);

// --- Custom edit lock (UI coordination — owned here) --------------------------------------
// Sentinel for the "creating a new item" row (has no id yet).
const CREATE = "__create__";
/** Which custom row is open: an item id, the CREATE sentinel, or null when idle. */
const editing = ref<string | null>(null);
const isLocked = computed(() => editing.value !== null);
const isCreating = computed(() => editing.value === CREATE);

function startCreate(): void {
  if (isLocked.value) return;
  editing.value = CREATE;
}

function startEdit(id: string): void {
  if (isLocked.value) return;
  editing.value = id;
}

function cancelEdit(): void {
  editing.value = null;
}

function saveCreate(name: string): void {
  addCustom(name);
  editing.value = null;
}

function saveEdit(id: string, name: string): void {
  renameCustom(id, name);
  editing.value = null;
}

/** Removing the open row also clears the lock; other custom removals are blocked while locked. */
function removeCustomRow(id: string): void {
  if (isLocked.value && editing.value !== id) return;
  removeCustom(id);
  if (editing.value === id) editing.value = null;
}

/** The editing text to seed the inline editor with (empty for create). */
const editingItemName = computed(() => {
  if (editing.value == null || editing.value === CREATE) return "";
  return customItems.value.find((item) => item.id === editing.value)?.name ?? "";
});

// --- Add-premade modal --------------------------------------------------------------------
const isPremadeModalOpen = ref(false);

/**
 * Whether there are any premade masterdata options to manage. The modal is the source of truth
 * for the selection (it can add AND remove), so the button opens it whenever options exist —
 * it is only disabled when there is nothing to choose from at all.
 */
const hasPremadeOptions = computed(() => props.masterdataOptions.length > 0);

function openPremadeModal(): void {
  isPremadeModalOpen.value = true;
}

function closePremadeModal(): void {
  isPremadeModalOpen.value = false;
}

/**
 * The modal emits the COMPLETE desired premade selection, so reconcile against it: new codes
 * are added, previously-added codes that are no longer selected are removed.
 */
function confirmAddPremade(options: PremadeOption[]): void {
  syncPremade(options);
  closePremadeModal();
}
</script>

<template>
  <div
    class="flex w-full flex-col overflow-hidden rounded-lg border border-neutral-60 bg-white"
    data-testid="premade-custom-editor"
  >
    <!-- Premade section -->
    <section class="flex flex-col justify-center gap-2 p-3" data-testid="premade-section">
      <div class="flex items-center gap-4">
        <div class="flex min-w-0 flex-1 flex-col gap-1">
          <h3 class="font-bold text-[14px] leading-[18px] text-text-100">
            {{ $t("experience.highlights.v2.premade.title") }}
          </h3>
          <p class="text-[14px] leading-[19px] text-text-90">
            {{ $t("experience.highlights.v2.premade.description") }}
          </p>
        </div>
        <NovaButton
          v-if="!readonly"
          variant="contained"
          size="xs"
          class="h-6 shrink-0 rounded-[8px]! px-3 py-1"
          :disabled="!hasPremadeOptions"
          data-testid="premade-open-modal"
          @click="openPremadeModal"
        >
          <NovaIcon name="plus" :size="16" class="mr-2" />
          {{ $t("experience.highlights.v2.premade.cta") }}
        </NovaButton>
      </div>

      <PremadeCustomEditorList
        v-if="premadeItems.length > 0"
        :items="premadeItems"
        :readonly="readonly"
        testid="premade-list"
        @reorder="reorderPremade($event as typeof premadeItems)"
        @remove="removePremade"
      />
    </section>

    <!-- Custom section -->
    <section class="flex flex-col justify-center gap-2 border-t border-neutral-60 p-3" data-testid="custom-section">
      <div class="flex items-center gap-4">
        <div class="flex min-w-0 flex-1 flex-col gap-1">
          <h3 class="font-bold text-[14px] leading-[18px] text-text-100">
            {{ $t("experience.highlights.v2.custom.title") }}
          </h3>
          <p class="text-[14px] leading-[19px] text-text-90">
            {{ $t("experience.highlights.v2.custom.description") }}
          </p>
        </div>
        <NovaButton
          v-if="!readonly"
          variant="outline"
          size="xs"
          class="h-6 shrink-0 rounded-[8px]! px-3 py-1"
          :disabled="isLocked"
          data-testid="custom-add"
          @click="startCreate"
        >
          <NovaIcon name="plus" :size="14" class="mr-2" />
          {{ $t("experience.highlights.v2.custom.cta") }}
        </NovaButton>
      </div>

      <div v-if="customItems.length > 0 || isCreating" class="flex flex-col gap-1">
        <!-- Saved rows: drag-reorderable, but locked while any item is being edited/created. -->
        <PremadeCustomEditorList
          v-if="customItems.length > 0"
          :items="customItems"
          :readonly="readonly"
          editable
          :locked-id="editing"
          testid="custom-list"
          @reorder="reorderCustom($event as typeof customItems)"
          @remove="removeCustomRow"
          @edit="startEdit"
        >
          <template #editing="{ item }">
            <HighlightItemEditor
              :initial-text="editingItemName"
              :index="item.visualization_order"
              data-testid="custom-item-editing"
              @save="saveEdit(item.id, $event)"
              @cancel="cancelEdit"
            />
          </template>
        </PremadeCustomEditorList>

        <!-- Inline create row: hidden until "Add custom" is clicked, appended after the list -->
        <HighlightItemEditor
          v-if="!readonly && isCreating"
          :index="customItems.length + 1"
          data-testid="custom-create-row"
          @save="saveCreate"
          @cancel="cancelEdit"
        />
      </div>
    </section>

    <!-- Add-premade modal -->
    <AddPremadeModal
      :show="isPremadeModalOpen"
      :masterdata-options="masterdataOptions"
      :hierarchical-groups="hierarchicalGroups"
      :added-codes="addedPremadeCodes"
      @close="closePremadeModal"
      @add="confirmAddPremade"
    />
  </div>
</template>
